import React, { useMemo } from 'react';
import {
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, { Circle, G, Line, Path, Polyline, Rect, Text as SvgText } from 'react-native-svg';
import { useTheme, makeThemedStyles, DISPLAY_FONT, SECTOR_COLOR, SECTOR_NAME } from '../v10/theme';
import { Track, GlassCard } from '../v10/ui';
import type { RiverModel, RiverItem } from '../lib/flo-weights';
import type { FlowResult } from '../lib/flo';

interface FloStatsDeckProps {
  model: RiverModel;
  flow: FlowResult;
  flowSeries: { date: string; score: number }[];
}

export const FloStatsDeck: React.FC<FloStatsDeckProps> = ({ model, flow, flowSeries }) => {
  const styles = useStyles();
  return (
    <View style={styles.container}>
      <FlowReportCard flow={flow} />
      <FlowOverTimeCard flowSeries={flowSeries} />
      <LoadBySectorCard model={model} />
      <HeaviestOffendersCard offenders={model.offenders} />
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Card 1: Flow Report
// ─────────────────────────────────────────────────────────────────────────────

function FlowReportCard({ flow }: { flow: FlowResult }) {
  const { t } = useTheme();
  const styles = useStyles();
  const bandDisplay = useMemo(() => {
    switch (flow.band) {
      case 'blue':
        return { label: 'Surplus', color: t.goldBright };
      case 'green':
        return { label: 'Healthy', color: t.gold };
      case 'orange':
        return { label: 'Congested', color: t.behind };
      case 'red':
        return { label: 'Blocked', color: t.lapsed };
    }
  }, [flow.band, t]);

  return (
    <GlassCard style={styles.card}>
      <Text style={styles.cardTitle}>Flow Report</Text>
      <View style={styles.flowReportBody}>
        <View style={styles.scoreSection}>
          <Text style={styles.scoreValue}>{flow.score.toFixed(1)}</Text>
          <View style={[styles.bandChip, { backgroundColor: bandDisplay.color }]}>
            <Text style={styles.bandChipText}>{bandDisplay.label}</Text>
          </View>
        </View>
        <View style={styles.miniBarsSection}>
          <MiniBar label="Cycle" value={flow.cycle} max={100} />
          <MiniBar label="Exchange" value={flow.exchange} max={150} />
          <MiniBar label="Steadiness" value={flow.steadiness} max={100} />
          <MiniBar label="Latency" value={flow.latency} max={100} />
        </View>
      </View>
    </GlassCard>
  );
}

function MiniBar({ label, value, max }: { label: string; value: number; max: number }) {
  const { t } = useTheme();
  const styles = useStyles();
  const percent = Math.min(100, (value / max) * 100);
  return (
    <View style={styles.miniBarContainer}>
      <Text style={styles.miniBarLabel}>{label}</Text>
      <Track pct={percent / 100} height={6} />
      <Text style={styles.miniBarValue}>{value.toFixed(0)}</Text>
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Card 2: Flow Over Time (Line Chart)
// ─────────────────────────────────────────────────────────────────────────────

function FlowOverTimeCard({ flowSeries }: { flowSeries: { date: string; score: number }[] }) {
  const { t } = useTheme();
  const styles = useStyles();
  if (flowSeries.length === 0) {
    return (
      <GlassCard style={styles.card}>
        <Text style={styles.cardTitle}>Flow Over Time</Text>
        <Text style={styles.emptyText}>No data yet</Text>
      </GlassCard>
    );
  }

  return (
    <GlassCard style={styles.card}>
      <Text style={styles.cardTitle}>Flow Over Time</Text>
      <View style={styles.cardBody}>
        <LineChartSvg data={flowSeries} width={280} color={t.gold} />
      </View>
    </GlassCard>
  );
}

function LineChartSvg({
  data,
  width,
  color,
}: {
  data: { date: string; score: number }[];
  width: number;
  color: string;
}) {
  const { t } = useTheme();
  const height = 200;
  const padLeft = 34;
  const padRight = 10;
  const padTop = 10;
  const padBottom = 22;
  const plotW = Math.max(1, width - padLeft - padRight);
  const plotH = height - padTop - padBottom;
  const maxVal = Math.max(1, ...data.map((d) => d.score));
  const xStep = data.length > 1 ? plotW / (data.length - 1) : 0;
  const points = data.map((d, i) => ({
    x: padLeft + (data.length > 1 ? i * xStep : plotW / 2),
    y: padTop + plotH - (d.score / maxVal) * plotH,
  }));
  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
  const labelStep = Math.max(1, Math.ceil(data.length / 6));

  return (
    <Svg width={width} height={height}>
      {[0, 0.5, 1].map((frac) => {
        const y = padTop + plotH * (1 - frac);
        return (
          <G key={frac}>
            <Line
              x1={padLeft}
              y1={y}
              x2={padLeft + plotW}
              y2={y}
              stroke={t.lineFaint}
              strokeWidth={1}
            />
            <SvgText x={padLeft - 6} y={y + 3} fontSize={9} fill={t.ivorySoft} textAnchor="end">
              {Math.round(maxVal * frac)}
            </SvgText>
          </G>
        );
      })}
      <Line
        x1={padLeft}
        y1={padTop + plotH}
        x2={padLeft + plotW}
        y2={padTop + plotH}
        stroke={t.ivoryFaint}
        strokeWidth={1}
      />
      {data.length > 0 && <Polyline points={polylinePoints} fill="none" stroke={color} strokeWidth={2} />}
      {points.map((p, i) => (
        <Circle key={i} cx={p.x} cy={p.y} r={2.6} fill={color} />
      ))}
      {data.map((d, i) =>
        i % labelStep === 0 || i === data.length - 1 ? (
          <SvgText
            key={`lbl-${i}`}
            x={points[i].x}
            y={height - 6}
            fontSize={9}
            fill={t.ivorySoft}
            textAnchor="middle"
          >
            {d.date.slice(-5)}
          </SvgText>
        ) : null
      )}
    </Svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Card 3: Load By Sector (Pie Chart)
// ─────────────────────────────────────────────────────────────────────────────

function LoadBySectorCard({ model }: { model: RiverModel }) {
  const { t } = useTheme();
  const styles = useStyles();
  const sectorColors: Record<string, string> = SECTOR_COLOR;

  const sectorNames: Record<string, string> = SECTOR_NAME;

  const pieData = useMemo(() => {
    return Object.entries(model.bySector || {})
      .map(([sectorKey, value]) => ({
        name: sectorNames[sectorKey] || sectorKey,
        value: Math.round(value),
        color: sectorColors[sectorKey] || t.ivorySoft,
      }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [model.bySector, t]);

  if (pieData.length === 0) {
    return (
      <GlassCard style={styles.card}>
        <Text style={styles.cardTitle}>Load by Sector</Text>
        <Text style={styles.emptyText}>No data yet</Text>
      </GlassCard>
    );
  }

  return (
    <GlassCard style={styles.card}>
      <Text style={styles.cardTitle}>Load by Sector</Text>
      <View style={styles.cardBody}>
        <PieChartSvg data={pieData} width={280} />
      </View>
    </GlassCard>
  );
}

function PieChartSvg({
  data,
  width,
}: {
  data: { name: string; value: number; color: string }[];
  width: number;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const size = Math.min(width, 220);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 8;
  const total = data.reduce((s, d) => s + d.value, 0);

  let cursor = -Math.PI / 2;
  const slices = data.map((d) => {
    const frac = total > 0 ? d.value / total : 0;
    const angle = frac * Math.PI * 2;
    const start = cursor;
    const end = cursor + angle;
    cursor = end;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const largeArc = angle > Math.PI ? 1 : 0;
    const path =
      total > 0 && frac > 0
        ? `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`
        : '';
    return { ...d, path, frac };
  });

  return (
    <View style={{ alignItems: 'center', gap: 10 }}>
      <Svg width={size} height={size}>
        {total <= 0 ? (
          <Circle cx={cx} cy={cy} r={r} fill={t.track} />
        ) : (
          slices.map((s, i) =>
            s.path ? <Path key={i} d={s.path} fill={s.color} /> : null
          )
        )}
      </Svg>
      <View style={styles.legendRow}>
        {data.map((it) => (
          <View key={it.name} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: it.color }]} />
            <Text style={styles.legendText} numberOfLines={1}>
              {it.name} ({it.value})
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Card 4: Heaviest Offenders (List)
// ─────────────────────────────────────────────────────────────────────────────

function HeaviestOffendersCard({ offenders }: { offenders: RiverItem[] }) {
  const { t } = useTheme();
  const styles = useStyles();
  if (offenders.length === 0) {
    return (
      <GlassCard style={styles.card}>
        <Text style={styles.cardTitle}>Heaviest Offenders</Text>
        <Text style={styles.emptyText}>No data yet</Text>
      </GlassCard>
    );
  }

  return (
    <GlassCard style={styles.card}>
      <Text style={styles.cardTitle}>Heaviest Offenders</Text>
      <View style={styles.cardBody}>
        {offenders.map((item, index) => (
          <OffenderRow key={item.id} rank={index + 1} item={item} />
        ))}
      </View>
    </GlassCard>
  );
}

function OffenderRow({ rank, item }: { rank: number; item: RiverItem }) {
  const { t } = useTheme();
  const styles = useStyles();
  const stateColors: Record<string, string> = {
    paused: t.ivoryFaint,
    unprocessed: t.ivorySoft,
    scheduled: t.gold,
    overdue: t.behind,
  };

  const stateDisplay: Record<string, string> = {
    paused: 'Paused',
    unprocessed: 'Unprocessed',
    scheduled: 'Scheduled',
    overdue: 'Overdue',
  };

  const tintColor = item.isStone ? t.goldDeep : stateColors[item.state] || t.ivorySoft;

  return (
    <View style={styles.offenderRow}>
      <Text style={styles.offenderRank}>{rank}</Text>
      <View style={styles.offenderInfo}>
        <Text style={styles.offenderTitle} numberOfLines={1}>
          {item.title}
        </Text>
        <Text style={styles.offenderListName} numberOfLines={1}>
          {item.listName}
        </Text>
      </View>
      <View style={[styles.weightBadge, { backgroundColor: tintColor }]}>
        <Text style={styles.weightBadgeText}>{item.weight.toFixed(1)}</Text>
      </View>
      <View style={[styles.stateChip, { backgroundColor: tintColor }]}>
        <Text style={styles.stateChipText}>{item.isStone ? `${item.daysOverdue}d` : stateDisplay[item.state] || item.state}</Text>
      </View>
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
  container: {
    gap: 12,
  },
  card: {
    padding: 16,
    gap: 4,
  },
  cardTitle: {
    fontSize: 14,
    color: t.ivory,
    fontFamily: DISPLAY_FONT,
    letterSpacing: 2.5,
    textTransform: 'uppercase',
  },
  cardBody: {
    marginTop: 8,
    alignItems: 'center',
  },
  emptyText: {
    color: t.ivorySoft,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 20,
    fontSize: 12,
  },

  // Flow Report styles
  flowReportBody: {
    width: '100%',
    gap: 16,
  },
  scoreSection: {
    alignItems: 'center',
    gap: 12,
  },
  scoreValue: {
    fontSize: 42,
    fontWeight: '900',
    color: t.goldBright,
  },
  bandChip: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 999,
  },
  bandChipText: {
    fontSize: 12,
    fontWeight: '700',
    color: t.onGold,
  },
  miniBarsSection: {
    width: '100%',
    gap: 10,
  },
  miniBarContainer: {
    width: '100%',
  },
  miniBarLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: t.ivory,
    marginBottom: 4,
  },
  miniBarValue: {
    fontSize: 10,
    fontWeight: '600',
    color: t.ivorySoft,
    marginTop: 2,
  },

  // Legend styles
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
    marginTop: 4,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    maxWidth: 140,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendText: {
    fontSize: 11,
    color: t.ivory,
  },

  // Offender row styles
  offenderRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: t.lineFaint,
  },
  offenderRank: {
    fontSize: 14,
    fontWeight: '700',
    color: t.ivorySoft,
    width: 24,
  },
  offenderInfo: {
    flex: 1,
    gap: 2,
  },
  offenderTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: t.ivory,
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
  },
  offenderListName: {
    fontSize: 10,
    color: t.ivorySoft,
  },
  weightBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  weightBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: t.onGold,
  },
  stateChip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
  },
  stateChipText: {
    fontSize: 10,
    fontWeight: '700',
    color: t.onGold,
  },
}),
);
